import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Phone, Plus, X } from '../../icons/wp-icons';
import { createKundeTelefon, deleteKundeTelefon } from '../../api/kundenApi';

function KundenForm({ kunde, onSubmit, onCancel, isLoading, hideStatusPicker, hasAnsprechpartner }) {
  const { user } = useAuth();
  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    telefon: '',
    adresse: '',
    ustNummer: '',
    status: 'neukunde',
    kundenTyp: 'STANDARD',
    info: '',
    kundennummer: ''
  });
  const [errors, setErrors] = useState({});

  // Sekundärnummern (Multi-Tel) — gleiche Daten/Routen wie die Kundendetailseite.
  // Beim Bearbeiten (kunde.id vorhanden) werden Nummern sofort per API
  // persistiert; beim Neuanlegen (noch keine id) lokal gesammelt und beim
  // Speichern als `zusatzTelefon`-Array mitgeschickt (Backend legt sie an).
  const isEdit = !!kunde?.id;
  const [zusatzTelefone, setZusatzTelefone] = useState([]);
  const [neueNummer, setNeueNummer] = useState('');
  const [neuesLabel, setNeuesLabel] = useState('');
  const [phoneAddOpen, setPhoneAddOpen] = useState(false);
  const [phoneSaving, setPhoneSaving] = useState(false);
  const [phoneError, setPhoneError] = useState('');

  // Populate form if editing existing kunde.
  // Platzhalter-E-Mails (enden mit @temp.local) werden NICHT ins Input-Feld
  // vorausgefüllt — sonst müsste der Admin den Platzhalter erst löschen,
  // bevor er die echte Adresse tippen kann.
  useEffect(() => {
    if (kunde) {
      const isPlaceholderEmail = kunde.email && kunde.email.endsWith('@temp.local');
      setFormData({
        name: kunde.name || '',
        email: isPlaceholderEmail ? '' : (kunde.email || ''),
        telefon: kunde.telefon || '',
        adresse: kunde.adresse || '',
        ustNummer: kunde.ustNummer || '',
        status: kunde.status || 'neukunde',
        kundenTyp: kunde.kundenTyp || 'STANDARD',
        info: kunde.info || '',
        kundennummer: kunde.kundennummer || ''
      });
      setZusatzTelefone(kunde.zusatzTelefon || []);
    }
  }, [kunde]);

  const handleAddPhone = async () => {
    const nummer = neueNummer.trim();
    if (!nummer) return;
    const label = neuesLabel.trim() || null;
    setPhoneError('');

    if (isEdit) {
      // Bestehender Kunde → sofort persistieren (wie auf der Detailseite).
      setPhoneSaving(true);
      try {
        const created = await createKundeTelefon(kunde.id, { nummer, label });
        setZusatzTelefone(prev => [...prev, created]);
      } catch (e) {
        console.error('Sekundärnummer anlegen fehlgeschlagen:', e);
        setPhoneError('Nummer konnte nicht gespeichert werden.');
        return;
      } finally {
        setPhoneSaving(false);
      }
    } else {
      // Neuanlage → lokal sammeln, wird beim Speichern mitgeschickt.
      setZusatzTelefone(prev => [...prev, { _localId: `${Date.now()}-${prev.length}`, nummer, label }]);
    }
    setNeueNummer('');
    setNeuesLabel('');
    setPhoneAddOpen(false);
  };

  const handleRemovePhone = async (entry) => {
    setPhoneError('');
    if (isEdit && entry.id) {
      try {
        await deleteKundeTelefon(kunde.id, entry.id);
      } catch (e) {
        console.error('Sekundärnummer löschen fehlgeschlagen:', e);
        setPhoneError('Nummer konnte nicht gelöscht werden.');
        return;
      }
      setZusatzTelefone(prev => prev.filter(t => t.id !== entry.id));
    } else {
      setZusatzTelefone(prev => prev.filter(t => t !== entry));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name required
    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    // E-Mail ist optional — nur Format validieren, wenn etwas eingegeben wurde
    if (formData.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Ungültige E-Mail-Adresse';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      // Beim Neuanlegen die lokal gesammelten Nummern mitschicken; im
      // Edit-Modus sind sie bereits per API persistiert → nicht erneut senden.
      const pendingPhones = isEdit
        ? []
        : zusatzTelefone.map(t => ({ nummer: t.nummer, label: t.label || null }));
      onSubmit({ ...formData, zusatzTelefon: pendingPhones });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name */}
      <div>
        <label className="block text-sm font-medium mb-2">
          Name <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.name ? 'border-danger' : 'border-border'
          }`}
          placeholder="Max Mustermann"
          disabled={isLoading}
        />
        {errors.name && (
          <p className="text-danger text-sm mt-1">{errors.name}</p>
        )}
      </div>

      {/* Email */}
      <div>
        <label className="block text-sm font-medium mb-2">
          E-Mail
        </label>
        <input
          type="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
            errors.email ? 'border-danger' : 'border-border'
          }`}
          placeholder="max@beispiel.de"
          disabled={isLoading}
        />
        {errors.email && (
          <p className="text-danger text-sm mt-1">{errors.email}</p>
        )}
      </div>

      {/* Telefon */}
      <div>
        <label className="block text-sm font-medium mb-2">Telefon</label>
        <input
          type="tel"
          name="telefon"
          value={formData.telefon}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="0123 456789"
          disabled={isLoading}
        />

        {/* Weitere Nummern (Sekundärnummern) — wie auf der Kundendetailseite.
            Werden von Kiki beim Caller-ID-Lookup zusätzlich konsultiert. */}
        <div className="mt-2 space-y-1">
          {zusatzTelefone.map((t, idx) => (
            <div key={t.id || t._localId || idx} className="flex items-center gap-2 text-text-muted text-sm group">
              <Phone className="w-3.5 h-3.5 opacity-60" />
              <span>{t.nummer}</span>
              {t.label && <span className="text-xs text-text-muted/70 italic">({t.label})</span>}
              <button
                type="button"
                onClick={() => handleRemovePhone(t)}
                className="opacity-0 group-hover:opacity-100 transition-opacity text-text-muted hover:text-red-600 ml-1"
                title="Nummer entfernen"
                disabled={isLoading}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}

          {phoneAddOpen ? (
            <div className="flex items-center gap-2 pt-1 flex-wrap">
              <Phone className="w-3.5 h-3.5 opacity-60" />
              <input
                type="tel"
                value={neueNummer}
                onChange={(e) => setNeueNummer(e.target.value)}
                placeholder="Telefonnummer"
                className="text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus:border-primary"
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPhone(); } }}
                disabled={isLoading || phoneSaving}
              />
              <input
                type="text"
                value={neuesLabel}
                onChange={(e) => setNeuesLabel(e.target.value)}
                placeholder="Label (optional)"
                className="text-sm border border-gray-300 rounded px-2 py-1 w-32 focus:outline-none focus:border-primary"
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPhone(); } }}
                disabled={isLoading || phoneSaving}
              />
              <button
                type="button"
                onClick={handleAddPhone}
                disabled={phoneSaving || !neueNummer.trim() || isLoading}
                className="text-sm text-primary hover:text-primary-dark disabled:opacity-50"
              >
                {phoneSaving ? 'Speichert…' : 'Übernehmen'}
              </button>
              <button
                type="button"
                onClick={() => { setPhoneAddOpen(false); setNeueNummer(''); setNeuesLabel(''); setPhoneError(''); }}
                className="text-sm text-text-muted hover:text-text"
                disabled={isLoading || phoneSaving}
              >
                Abbrechen
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setPhoneAddOpen(true)}
              className="text-xs text-text-muted hover:text-primary flex items-center gap-1 mt-0.5"
              disabled={isLoading}
            >
              <Plus className="w-3 h-3" />
              weitere Nummer hinzufügen
            </button>
          )}
          {phoneError && <p className="text-danger text-sm">{phoneError}</p>}
        </div>
      </div>

      {/* Adresse */}
      <div>
        <label className="block text-sm font-medium mb-2">Adresse</label>
        <input
          type="text"
          name="adresse"
          value={formData.adresse}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Musterstraße 123, 12345 Musterstadt"
          disabled={isLoading}
        />
      </div>

      {/* UST-Nummer (gewerbliche Kunden) */}
      <div>
        <label className="block text-sm font-medium mb-2">UST-IdNr (gewerblich)</label>
        <input
          type="text"
          name="ustNummer"
          value={formData.ustNummer}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="DE123456789 (optional)"
          disabled={isLoading}
        />
        <p className="text-xs text-text-muted mt-1">Nur für gewerbliche Kunden erforderlich</p>
      </div>

      {/* Kundennummer — Backend-Trigger vergibt automatisch fortlaufend,
          wenn das Feld leer bleibt: HeyKiki ab 101001, WerkPilot ab 10001.
          Manuelles Setzen ist erlaubt, z.B. für Daten-Imports aus
          Vorgängersystemen. */}
      <div>
        <label className="block text-sm font-medium mb-2">Kundennummer</label>
        <input
          type="text"
          name="kundennummer"
          value={formData.kundennummer}
          onChange={handleChange}
          className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="leer lassen — wird automatisch vergeben"
          disabled={isLoading}
        />
        <p className="text-xs text-text-muted mt-1">
          {isHeykikiOrg
            ? 'Wird automatisch fortlaufend vergeben (ab 101001). Manuell überschreiben nur bei Bestandsdaten-Übernahme.'
            : 'Wird in WerkPilot automatisch fortlaufend vergeben (ab 10001). Manuell überschreiben nur bei Bestandsdaten-Übernahme.'}
        </p>
      </div>

      {/* Kundentyp */}
      {!hideStatusPicker && (
      <div>
        <label className="block text-sm font-medium mb-2">Kundentyp</label>
        <div className="flex gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => {
              if (kunde?.status === 'lieferant' && hasAnsprechpartner) return;
              setFormData(prev => ({ ...prev, status: 'neukunde', kundenTyp: 'STANDARD' }));
            }}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
              formData.status === 'neukunde' && formData.kundenTyp !== 'HAUSVERWALTUNG'
                ? 'bg-blue-600 text-white'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
            } ${kunde?.status === 'lieferant' && hasAnsprechpartner && formData.status === 'lieferant' ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isLoading}
          >
            Neukunde
          </button>
          <button
            type="button"
            onClick={() => {
              if (kunde?.status === 'lieferant' && hasAnsprechpartner) return;
              setFormData(prev => ({ ...prev, status: 'stammkunde', kundenTyp: 'STANDARD' }));
            }}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
              formData.status === 'stammkunde' && formData.kundenTyp !== 'HAUSVERWALTUNG'
                ? 'bg-primary text-white'
                : 'bg-primary-light text-primary hover:bg-teal-200'
            } ${kunde?.status === 'lieferant' && hasAnsprechpartner && formData.status === 'lieferant' ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isLoading}
          >
            Stammkunde
          </button>
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, status: 'lieferant', kundenTyp: 'STANDARD' }))}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
              formData.status === 'lieferant' && formData.kundenTyp !== 'HAUSVERWALTUNG'
                ? 'bg-amber-600 text-white'
                : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
            }`}
            disabled={isLoading}
          >
            Lieferant
          </button>
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, kundenTyp: 'HAUSVERWALTUNG' }))}
            className={`flex-1 py-2 px-4 rounded-lg font-medium transition ${
              formData.kundenTyp === 'HAUSVERWALTUNG'
                ? 'bg-purple-600 text-white'
                : 'bg-purple-100 text-purple-700 hover:bg-purple-200'
            }`}
            disabled={isLoading}
          >
            Hausverwaltung
          </button>
        </div>
        {kunde?.status === 'lieferant' && hasAnsprechpartner && (
          <p className="text-xs text-amber-600 mt-2">
            Kundentyp kann nicht geändert werden, solange Ansprechpartner verknüpft sind. Bitte entfernen Sie zuerst alle Ansprechpartner.
          </p>
        )}
      </div>
      )}

      {/* Info/Notizen */}
      <div>
        <label className="block text-sm font-medium mb-2">Notizen</label>
        <textarea
          name="info"
          value={formData.info}
          onChange={handleChange}
          rows="4"
          className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Zusätzliche Informationen zum Kunden..."
          disabled={isLoading}
        />
      </div>

      {/* Buttons */}
      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          className="flex-1 bg-primary text-white py-3 rounded-button font-medium hover:bg-primary-dark transition disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? 'Wird gespeichert...' : kunde ? 'Aktualisieren' : 'Erstellen'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="flex-1 bg-gray-200 text-text py-3 rounded-button font-medium hover:bg-gray-300 transition disabled:opacity-50"
          disabled={isLoading}
        >
          Abbrechen
        </button>
      </div>
    </form>
  );
}

export default KundenForm;
